/**
 * Funnel Analytics Component
 *
 * Provides comprehensive analytics and insights for a specific funnel.
 * This component displays:
 * - Key performance indicators (KPIs) for funnel performance
 * - Conversion rates and lead generation metrics
 * - Time-based trends with customizable date ranges
 * - Version comparison analytics
 * - Lead source distribution
 * - Site visit statistics
 * - Integration with lead service performance
 * - Geographic analysis including blocked postal codes
 *
 * The component features interactive charts, filters, and data visualization
 * tools to help users understand funnel performance and make optimization decisions.
 *
 * @module components/analytics/funnel-analytics
 */

import moment from "moment";
import { useOutletContext, useParams } from "react-router";
import React, { useRef, useEffect, useState, useCallback } from "react";
import { Row, Col, Card, CardGroup, ButtonGroup } from "react-bootstrap";
import {
  RADIOS,
  RADIO_RANGE,
  transformData,
  findRangeMode,
  amountFormatter,
  DATE_RANGE_MODE,
  handleOnRangeChange,
  handleRadioOnSelect,
  handlePickerLeftButton,
  handlePickerRightButton
} from "./analytics-helper";
import Drawer from "../drawer";
import Overlay from "../overlay";
import KPITile from "./kpi-tile";
import Loader from "../loader/loader";
import {
  fetchFunnelInsights,
  fetchLeadServiceInsight,
  fetchFbAdLeadsByFunnelId,
  fetchLeadResponseInsight,
  fetchFunnelInsightVersions,
  fetchFunnelNotSuitableCCLeads
} from "../../services/api-declaration";
import PieChart from "../charts/pieChart";
import SunburstChart from "./sunburst-chart";
import { LS_TYPE_FILTERS } from "../strings";
import { SiteVisitsChart } from "./site-visits-chart";
import DatePickerLimitRange from "./date-range-picker";
import { showError } from "../../services/toast-service";
import FunnelAdsEvaluation from "./funnel-ads-evaluation";
import AIInterviewAnalytics from "./ai-interview-analytics";
import { CustomCard, ToggleBtn } from "../common-styling";
import LeadServiceAnalytics from "./lead-service-analytics";
import { notSuitableReasons } from "../leads/deal-stages-hash";
import FunnelVersionsAnalytics from "./funnel-versions-analytics";
import VersionDropdown from "../reusable-form-fields/version-dropdown";
import BlockedPostalCode from "../blocked-postal-code/blocked-postal-code";
import { formatLeadServiceInsightsForBarChart } from "../../services/common-service";
import BlockedPostalCodeCount from "../blocked-postal-code/blocked-postal-code-count";
import { useTranslation } from "react-i18next";
import "./lead-details.css";
import "./funnel-analytics.css";

/**
 * Color configuration for different graph types
 * Each graph type has a main color and an area fill color
 */
const graphColor = {
  allLandingVisitsCount: {
    color: "rgb(156 204 101)",
    areaColor: "rgb(241 248 233)"
  },
  landingPage: { color: "rgb(0 120 255)", areaColor: "rgba(0,120,255,0.06)" },
  leadCreated: { color: "rgb(32 201 151)", areaColor: "rgba(32,201,151,0.06)" },
  fbAdLeads: { color: "rgb(38, 198, 218)", areaColor: "rgb(224 247 250)" },
  websiteVisits: { color: "rgb(173 0 255)", areaColor: "rgba(173,0,255,0.06)" },
  phoneVisits: {
    color: "rgb(255, 173, 0)",
    areaColor: "rgba(255, 173, 0, 0.06)"
  },
  emailVisits: {
    color: "rgb(0, 255, 173)",
    areaColor: "rgba(0, 255, 173, 0.06)"
  }
};

/**
 * Color mapping for different lead source types
 * Used in conversion rate and lead source charts
 */
const LEAD_COLOR = {
  COCKPIT: "#20c997", // Funnel cockpit leads
  FB_COCKPIT: "#1878f3", // Facebook leads through cockpit
  FB_AD: "#053F5C", // Direct Facebook ad leads
  NOT_CONVERTED: "grey", // Non-converted visitors
  UNCATEGORIZED: "#ff8a25" // Leads without a clear source
};

/**
 * Main Funnel Analytics component
 * Displays comprehensive analytics for a specific funnel
 *
 * @returns {JSX.Element} Funnel analytics dashboard
 */
const FunnelAnalytics = () => {
  const { t } = useTranslation();

  // References to DOM elements and persistent values
  const rootRef = useRef(); // Root element reference
  const chartRef = useRef(); // Chart element reference
  const dateRangeModeRef = useRef(DATE_RANGE_MODE.ALL_TIME); // Current date range mode

  // Route parameters and context
  const funnelId = useParams().id; // Current funnel ID from URL
  const { funnel, funnelVersions } = useOutletContext(); // Funnel data from parent

  // states
  const [dataState, setDataState] = useState([]);
  const [leadDetailsData, setLeadDetailsData] = useState();
  const [leadServiceData, setLeadServiceData] = useState({});
  const [ccNotSuitableLeadsData, setCCNotSuitableLeadsData] = useState();
  const [loading, setLoading] = useState(true);
  const [allLandingVisitsCount, setAllLandingVisitsCount] = useState();
  const [landingPage, setLandingPage] = useState();
  const [startedAt, setStartedAt] = useState("---");
  const [leadsCreated, setLeadsCreated] = useState();
  const [fbAdLeads, setFbAdLeads] = useState();
  const [websiteVisits, setWebsiteVisits] = useState();
  const [emailVisits, setEmailVisits] = useState();
  const [phoneVisits, setPhoneVisits] = useState();
  const [conversionRate, setConversionRate] = useState();
  const [allVersions, setAllVersions] = useState();
  const [radioValue, setRadioValue] = useState("1");
  const [hasData, setHasData] = useState(true);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedVersion, setSelectedVersion] = useState(
    funnelVersions?.currentVersion
  );
  const [showDrawer, setshowDrawer] = useState(false);
  const [lsType, setLsType] = useState(LS_TYPE_FILTERS.ALL);

  const handleRadioSelect = async (value) => {
    dateRangeModeRef.current = RADIO_RANGE[value];
    const [startDate, endDate] = handleRadioOnSelect({
      radioValue: value,
      dateRangeMode: dateRangeModeRef.current,
      radioValueSetter: setRadioValue,
      dateRangeSetter: setDateRange,
      selectedDateSetter: setSelectedDate
    });
    if (RADIO_RANGE[value] !== DATE_RANGE_MODE.RANGE) {
      await fetchFunnelInsightsData({ startDate, endDate });
    }
  };

  const convertKPIs = (kpis, startDate) => {
    setStartedAt(
      kpis.landingPage.graph[0]
        ? moment(kpis.landingPage.graph[0].startAt).format("Do MMMM YY")
        : startDate
    );
    const allLandingVisitsCount = createLineGraphConfig({
      graph: kpis.landingPage.graph,
      color: graphColor.allLandingVisitsCount.color,
      areaColor: graphColor.allLandingVisitsCount.areaColor,
      unit: kpis.landingPage.unit || "",
      count: kpis.landingPage.allLandingVisitsCount
    });
    const landingPage = createLineGraphConfig({
      graph: kpis.landingPage.graph,
      color: graphColor.landingPage.color,
      areaColor: graphColor.landingPage.areaColor,
      unit: kpis.landingPage.unit || "",
      count: kpis.landingPage.count
    });
    const leadsCreated = createLineGraphConfig({
      graph: kpis.leadCreated.graph,
      color: graphColor.leadCreated.color,
      areaColor: graphColor.leadCreated.areaColor,
      unit: kpis.leadCreated.unit || "",
      count: kpis.leadCreated.count
    });
    const fbAdLeads = createLineGraphConfig({
      graph: kpis.fbAdLeads.graph,
      color: graphColor.fbAdLeads.color,
      areaColor: graphColor.fbAdLeads.areaColor,
      unit: kpis.fbAdLeads.unit || "",
      count: kpis.fbAdLeads.count
    });
    const websiteVisits = createLineGraphConfig({
      graph: kpis.websiteVisits.graph,
      color: graphColor.websiteVisits.color,
      areaColor: graphColor.websiteVisits.areaColor,
      unit: kpis.websiteVisits.unit || "",
      count: kpis.websiteVisits.count
    });

    const emailVisits = createLineGraphConfig({
      graph: kpis.emailVisits.graph,
      color: graphColor.emailVisits.color,
      areaColor: graphColor.emailVisits.areaColor,
      unit: kpis.emailVisits.unit || "",
      count: kpis.emailVisits.count
    });

    const phoneVisits = createLineGraphConfig({
      graph: kpis.phoneVisits.graph,
      color: graphColor.phoneVisits.color,
      areaColor: graphColor.phoneVisits.areaColor,
      unit: kpis.phoneVisits.unit || "",
      count: kpis.phoneVisits.count
    });
    const dataset = kpis.conversionRate.graph.map((mp) => ({
      ...mp,
      color: LEAD_COLOR[mp._id]
    }));
    const conversionRate = createPieGraphConfig({
      dataset,
      count: kpis.conversionRate.count,
      unit: kpis.conversionRate.unit || ""
    });
    setHasData(Boolean(landingPage.count || leadsCreated.count));
    setAllLandingVisitsCount(allLandingVisitsCount);
    setLandingPage(landingPage);
    setLeadsCreated(leadsCreated);
    setFbAdLeads(fbAdLeads);
    setWebsiteVisits(websiteVisits);
    setEmailVisits(emailVisits);
    setPhoneVisits(phoneVisits);
    setConversionRate(conversionRate);
  };

  const handleLeftBtn = async () => {
    const [startDate, endDate] = handlePickerLeftButton({
      startRange: dateRange[0],
      dateRangeMode: dateRangeModeRef.current,
      dateRangeSetter: setDateRange,
      selectedDateSetter: setSelectedDate
    });
    await fetchFunnelInsightsData({ startDate, endDate });
  };

  const handleRightBtn = async () => {
    const [startDate, endDate] = handlePickerRightButton({
      endRange: dateRange[1],
      dateRangeMode: dateRangeModeRef.current,
      dateRangeSetter: setDateRange,
      selectedDateSetter: setSelectedDate
    });
    if (startDate && endDate) {
      await fetchFunnelInsightsData({ startDate, endDate });
    }
  };

  const fetchFunnelInsightsData = async ({
    startDate = new Date(),
    endDate = new Date(),
    version = funnelVersions?.currentVersion
  } = {}) => {
    if (!funnelVersions) {
      return;
    }
    setLoading(true);
    let dateRangeMode = dateRangeModeRef.current;
    if (dateRangeMode === DATE_RANGE_MODE.RANGE) {
      dateRangeMode = findRangeMode({ startDate, endDate });
    }
    endDate = new Date(endDate);
    endDate.setDate(endDate.getDate() + 1);
    if (dateRangeModeRef.current === DATE_RANGE_MODE.ALL_TIME) {
      startDate = endDate = undefined;
    }
    setSelectedVersion(version);
    try {
      const [
        { value: versionInsight },
        { value: funnelInsight },
        { value: fbAdLeadsInfo },
        { value: leadRespInsight },
        { value: leadServiceInsights },
        { value: ccNotSuitableLeadsData }
      ] = await Promise.allSettled([
        fetchFunnelInsightVersions(funnelId, {
          startDate,
          endDate,
          dateRangeMode
        }),
        fetchFunnelInsights(funnelId, {
          startDate,
          endDate,
          dateRangeMode,
          funnelVersion: version ? version._id : selectedVersion._id
        }),
        fetchFbAdLeadsByFunnelId(funnelId, {
          startDate,
          endDate,
          dateRangeMode
        }),
        fetchLeadResponseInsight(funnelId, {
          startDate,
          endDate,
          dateRangeMode,
          funnelVersion: version ? version._id : selectedVersion._id
        }),
        fetchLeadServiceInsight(funnelId, {
          lsType,
          startDate,
          endDate,
          dateRangeMode,
          funnelVersion: version ? version._id : selectedVersion._id
        }),
        fetchFunnelNotSuitableCCLeads(funnelId, {
          startDate,
          endDate,
          dateRangeMode,
          funnelVersion: version ? version._id : selectedVersion._id
        })
      ]);
      let kpis = undefined;
      createBarChartConfig(versionInsight.data.data);
      setLeadDetailsData(leadRespInsight.data.data);
      setLeadServiceData(
        formatLeadServiceInsightsForBarChart({
          collection: leadServiceInsights.data.data
        })
      );
      setCCNotSuitableLeadsData(ccNotSuitableLeadsData.data.data);
      if (funnelInsight.data.data) {
        setDataState(funnelInsight.data.data.analyticsData);
        kpis = { ...funnelInsight.data.data.kpis };
      }
      if (fbAdLeadsInfo.data.data) {
        kpis = { ...kpis, fbAdLeads: fbAdLeadsInfo.data.data };
      }
      if (kpis) {
        convertKPIs(kpis, startDate?.toDateString());
      }
    } catch (error) {
      showError(
        error.response?.data.message || t("toastMessage.fetchInsightsError")
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchFunnelInsightsDataCB = useCallback(
    fetchFunnelInsightsData,
    // eslint-disable-next-line
    [funnelId, funnelVersions]
  );

  const calcDelta = (graph) => {
    let delta = 0;
    const elements = [...graph].slice(-2);
    if (elements.length === 2) {
      const maxVal = Math.max(elements[1].count, elements[0].count, 1);
      delta = elements[1].count - elements[0].count;
      delta = Math.round((delta / maxVal) * 100);
    }
    return delta;
  };

  const createPieGraphConfig = ({ dataset, unit, count }) => {
    const data = {
      datasets: [
        {
          data: dataset.map((d) => d.count),
          backgroundColor: dataset.map((d) => d.color)
        }
      ],
      labels: dataset.map((d) => ` ${d.label}`)
    };
    const options = {
      legend: { display: false },
      interaction: { intersect: false }
    };

    const config = { type: "pie", data: data, options: options };
    const amount = `${count || "---"}${unit}`;
    return { config, unit, count, amount };
  };

  const createLineGraphConfig = ({ graph, color, areaColor, unit, count }) => {
    const newGraph = transformData({
      graph,
      dateRangeMode: dateRangeModeRef.current
    });
    const delta = calcDelta(newGraph);

    const formatToolTipDate = (date) => {
      switch (dateRangeModeRef.current) {
        case DATE_RANGE_MODE.DAY:
          return `${moment(date).format("MMM Do YY HH")}h`;
        case DATE_RANGE_MODE.WEEK:
        case DATE_RANGE_MODE.MONTH:
          return moment(date).format("MMM Do YY");
        default:
          return moment(date).format("MMM YY");
      }
    };

    const data = {
      labels: newGraph.map((g) => formatToolTipDate(g.date)),
      datasets: [
        {
          label: `${unit ? " percent" : " count"}`,
          data: newGraph.map((g) => g.count),
          borderColor: color,
          backgroundColor: areaColor,
          fill: "start",
          pointStyle: "dash"
        }
      ]
    };
    const options = {
      scales: {
        xAxes: [
          {
            gridLines: { display: false },
            ticks: {
              callback: function () {
                return "";
              },
              color: "rgba(0, 0, 0, 0)"
            }
          }
        ],
        y: { min: 0, max: 50 },
        yAxes: [
          {
            gridLines: { display: false },
            ticks: {
              callback: function () {
                return "";
              },
              color: "rgba(0, 0, 0, 0)"
            }
          }
        ]
      },
      legend: { display: false },
      plugins: {
        filler: { propagate: false },
        title: {
          display: false,
          text: (ctx) => "Fill: " + ctx.chart.data.datasets[0].fill
        }
      },
      interaction: { intersect: false }
    };

    const config = { type: "line", data: data, options: options };

    const amount = count ? `${amountFormatter(count)}${unit}` : `---${unit}`;
    return { config, delta, unit, count, amount };
  };

  const createBarChartConfig = (versionsData) => {
    const dataSet = new Set();
    versionsData
      .flat()
      .forEach((mp) => dataSet.add(`Version ${mp.funnelVersion}`));
    const baseData = versionsData.map((mp) => mp.map((a) => a.count));
    const config = {
      type: "bar",
      data: {
        labels: Array.from(dataSet.values()),
        datasets: [
          {
            label: t("createdLeads"),
            data: baseData.map((v) => v[1]),
            backgroundColor: "#20c997",
            barPercentage: 0.1,
            barThickness: 56
          },
          {
            label: t("numberOfVisits"),
            data: baseData.map((v) => v[0]),
            backgroundColor: "#0078ff",
            barPercentage: 0.1,
            barThickness: 56
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        layout: { padding: 20 },
        scales: {
          xAxes: [{ stacked: true }],
          yAxes: [{ ticks: { beginAtZero: true } }]
        }
      }
    };
    setAllVersions({ config, versionsData });
    // return { config };
  };

  const _handleVersionChange = async (version) => {
    const [startDate, endDate] = dateRange;
    await fetchFunnelInsightsData({ startDate, endDate, version });
  };

  const onRangeChange = async (range) => {
    const [startDate, endDate] = handleOnRangeChange({
      range,
      dateRangeMode: dateRangeModeRef.current,
      dateRangeSetter: setDateRange,
      selectedDateSetter: setSelectedDate
    });
    await fetchFunnelInsightsData({ startDate, endDate });
  };
  const toggleDrawer = () => {
    setshowDrawer((showDrawer) => !showDrawer);
  };

  useEffect(() => {
    if (dateRangeModeRef.current !== DATE_RANGE_MODE.RANGE) {
      fetchFunnelInsightsDataCB();
    }
  }, [fetchFunnelInsightsDataCB]);

  useEffect(() => {
    if (!chartRef.current || !dataState) {
      return;
    }
    const chart = new SiteVisitsChart(chartRef.current);
    chart.draw(dataState, rootRef.current.clientWidth);
    return () => chart.destroy();
  }, [dataState]);

  return (
    <div ref={rootRef} className="w-100">
      <>
        <CustomCard className="sticky-top mt-4 kpi-date-range-header">
          <Card.Header className="bg-white kpi-tiles-header">
            <div className="kpi-date">( Started From: {startedAt} )</div>
            <div className="d-flex justify-content-end justify-self-end">
              {dateRangeModeRef.current !== DATE_RANGE_MODE.ALL_TIME && (
                <DatePickerLimitRange
                  mode={DATE_RANGE_MODE}
                  className="w-50"
                  placeholderText={RADIOS[radioValue - 1].name}
                  dateRangeMode={dateRangeModeRef.current}
                  maxDate={moment.now()}
                  selectedDate={selectedDate}
                  handleLeftBtn={handleLeftBtn}
                  handleRightBtn={handleRightBtn}
                  dateRange={dateRange}
                  onRangeChange={onRangeChange}
                />
              )}
              <ButtonGroup>
                {RADIOS.map((radio, idx) => (
                  <ToggleBtn
                    disabled={loading}
                    key={idx}
                    id={`radio-${idx}`}
                    type="radio"
                    className="kpi-toggle-button"
                    name="radio"
                    value={radio.value}
                    checked={radioValue === radio.value}
                    onChange={(e) => handleRadioSelect(e.currentTarget.value)}
                  >
                    {radio.name}
                  </ToggleBtn>
                ))}
              </ButtonGroup>
              <div className="kpi-version">
                <span className="kpi-version-label">Version:</span>
                <div className="mx-2">
                  <VersionDropdown
                    selectedVersion={selectedVersion}
                    funnelVersions={funnelVersions}
                    handleVersionChange={_handleVersionChange}
                  />
                </div>
              </div>
            </div>
          </Card.Header>
        </CustomCard>
        {/* <h5 className="my-4">Analytics</h5> */}
        <CustomCard className="mt-4">
          <CardGroup>
            {!loading &&
            leadsCreated &&
            conversionRate &&
            landingPage &&
            websiteVisits ? (
              <>
                <KPITile
                  bulletColor={graphColor.allLandingVisitsCount.color}
                  title={t("funnelInsights.allLandingVisits")}
                  data={allLandingVisitsCount.amount}
                />
                <KPITile
                  bulletColor={graphColor.landingPage.color}
                  title={t("funnelInsights.visits")}
                  data={landingPage.amount}
                  config={landingPage.config}
                  delta={landingPage.delta}
                />
                <KPITile
                  bulletColor={graphColor.leadCreated.color}
                  title="Funnel Leads"
                  data={leadsCreated.amount}
                  config={leadsCreated.config}
                  delta={leadsCreated.delta}
                />
                <KPITile
                  bulletColor={graphColor.fbAdLeads.color}
                  title="Lead Ads"
                  data={fbAdLeads?.amount}
                  config={fbAdLeads?.config}
                  delta={fbAdLeads?.delta}
                />
                <KPITile
                  bulletColor={graphColor.websiteVisits.color}
                  title={t("funnelInsights.websiteVisits")}
                  data={websiteVisits.amount}
                  config={websiteVisits.config}
                  delta={websiteVisits.delta}
                />
                <KPITile
                  bulletColor={graphColor.phoneVisits.color}
                  title={t("funnelInsights.phoneVisits")}
                  data={phoneVisits.amount}
                  config={phoneVisits.config}
                  delta={phoneVisits.delta}
                />
                <KPITile
                  bulletColor={graphColor.emailVisits.color}
                  title={t("funnelInsights.emailVisits")}
                  data={emailVisits.amount}
                  config={emailVisits.config}
                  delta={emailVisits.delta}
                />
                {/* commented tiles as they are not functional */}
                {/* <KPITile title={t("contact")} data={"---%"} />
                <KPITile title={t("completionRate")} data={"---%"} /> */}
                <KPITile
                  bulletColors={[
                    LEAD_COLOR.COCKPIT,
                    LEAD_COLOR.FB_AD,
                    LEAD_COLOR.FB_COCKPIT,
                    LEAD_COLOR.UNCATEGORIZED
                  ]}
                  title={t("conversionRate")}
                  data={conversionRate.amount}
                  config={conversionRate.config}
                />
              </>
            ) : (
              <Card style={{ height: 200 }}>
                <Overlay show={loading} />
              </Card>
            )}
          </CardGroup>
        </CustomCard>
        <BlockedPostalCodeCount
          funnelId={funnelId}
          toggleDrawer={toggleDrawer}
        />
        <CustomCard className="my-4">
          {loading ? (
            <Loader />
          ) : hasData ? (
            <div ref={chartRef} className="funnel-analytic" />
          ) : (
            <Card.Header className="bg-white kpi-tiles-header justify-content-center">
              <h6 className="mb-0 fw-bold p-5 align-self-center">
                Not Enough Data to show
              </h6>
            </Card.Header>
          )}
        </CustomCard>

        <AIInterviewAnalytics
          funnelId={funnelId}
          currentDateRangeMode={dateRangeModeRef.current}
          dateRange={dateRange}
        />

        <FunnelAdsEvaluation
          funnelId={funnelId}
          funnelVersion={selectedVersion?._id}
        />

        {funnel.leadService ? (
          <LeadServiceAnalytics
            data={leadServiceData}
            setData={setLeadServiceData}
            startedAt={startedAt}
            selectedLsType={lsType}
            setLsType={setLsType}
            selectedVersion={selectedVersion}
            currentDateRangeMode={dateRangeModeRef.current}
            dateRange={dateRange}
          />
        ) : (
          <></>
        )}

        <Row>
          <Col sm={12} md={6}>
            <CustomCard className="my-4">
              <Card.Header className="bg-white kpi-tiles-header">
                <h5 className="mb-0 fw-bold">All Versions Insight</h5>
                <div className="kpi-date">( Started From: {startedAt} )</div>
              </Card.Header>
              {allVersions && allVersions.versionsData.length ? (
                <FunnelVersionsAnalytics config={allVersions.config} />
              ) : (
                <h6 className="mb-0 fw-bold p-5 align-self-center">
                  Not Enough Data to show
                </h6>
              )}
            </CustomCard>
          </Col>
          <Col sm={12} md={6}>
            <CustomCard className="my-4">
              <Card.Header className="bg-white kpi-tiles-header">
                <h5 className="mb-0 fw-bold">{t("notSutablePie.title")}</h5>
              </Card.Header>
              <div style={{ height: 440 }}>
                {loading ? (
                  <div className="h-100 d-flex align-items-center justify-content-center">
                    <Loader />
                  </div>
                ) : ccNotSuitableLeadsData?.reasons?.length ? (
                  <div className="p-4 px-2 h-100 w-100">
                    <PieChart
                      chartWidth={"100%"}
                      chartHeight={"100%"}
                      series={ccNotSuitableLeadsData.reasons.map(
                        (item) => item.count
                      )}
                      labels={ccNotSuitableLeadsData.reasons.map(
                        (item) => notSuitableReasons[item.reason] || item.reason
                      )}
                      colors={[
                        "#0b8ffb",
                        "#22e396",
                        "#feb018",
                        "#fd4560",
                        "#775dd0",
                        "#2db5d3",
                        "#d37b2d",
                        "#d2362d",
                        "#a5d22d",
                        "#d2492d",
                        "#21c997",
                        "#455a64",
                        "#ff8a25",
                        "#7c4dff",
                        "#fc00ff",
                        "#9a8478",
                        "#dd5e89",
                        "#b29f94",
                        // extra colors
                        "#2193b0",
                        "#b21f1f",
                        "#c33764"
                      ]}
                      legendPosition="left"
                      tooltipFormatter={(_count, _) => _count}
                    />
                  </div>
                ) : (
                  <div className="h-100 d-flex align-items-center justify-content-center fw-bold">
                    {t("noData")}
                  </div>
                )}
              </div>
            </CustomCard>
          </Col>
        </Row>
        <CustomCard className="my-4">
          <Card.Header className="bg-white kpi-tiles-header">
            <h5 className="mb-0 fw-bold">Lead Ad Details</h5>
            <div className="kpi-date">( Started From: {startedAt} )</div>
          </Card.Header>
          <SunburstChart data={leadDetailsData} />
        </CustomCard>
        <Drawer
          showDrawer={showDrawer}
          toggleDrawer={toggleDrawer}
          drawerTitle={t("common.blockedPostalCode")}
          width="25"
        >
          <BlockedPostalCode />
        </Drawer>
      </>
    </div>
  );
};
export default FunnelAnalytics;
